import { useCallback, useEffect, useRef, useState } from 'react';
import { CITY_COORDS, resolveCityKey } from '../usPlaces';
import { locateCity } from '../locateCity';
import { cityForTimeZone } from './sceneModel';

// Which city the homepage hero shows, in order: a city picked here in this
// session, then a signed-in fan's saved city (users/{uid}.city, from sign-up
// or Fan Settings), then the choice remembered on this device, then a guess
// from the browser's time zone (no prompt, nothing sent anywhere). The viewer
// can pick another city or share their exact location from the hero's chip;
// either choice is remembered on this device. Only the city is kept, never the
// coordinates.
const STORAGE_KEY = 'nx_home_city_a1';

function readSaved() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    if (saved && CITY_COORDS[saved.key]) return saved;
  } catch {
    // Private mode or blocked storage: fall through to the time zone guess.
  }
  return null;
}

function save(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Not remembered; the choice still applies for this visit.
  }
}

function timeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
}

// Returns { cityKey, source, pick, locate, locating, locateError }.
// `profileCity` is the signed-in fan's saved city as they typed it ("Detroit",
// "Atlanta, GA"); resolveCityKey matches it to a tracked city, and anything it
// can't match is ignored. cityKey is null when there's no saved city, the time
// zone isn't a US one and nothing is stored; the page then falls back to the
// biggest scene.
export default function useHomeCity(profileCity) {
  const [place, setPlace] = useState(() => {
    const saved = readSaved();
    if (saved) return saved;
    const key = cityForTimeZone(timeZone());
    return key ? { key, source: 'tz' } : { key: null, source: null };
  });
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState('');
  // Set once the viewer picks a city or shares their location on this visit.
  // The saved city must not override that, or the hero's chip would stop
  // working - but it does override the same choice restored from a past visit.
  const chosenHere = useRef(false);

  // The profile loads after the first render (useViewer fetches it), so the
  // saved city is applied here rather than in the initial state. It isn't
  // written to localStorage: the next, signed-out viewer of a shared device
  // shouldn't inherit it.
  const profileKey = profileCity ? resolveCityKey({ city: profileCity }) : null;
  useEffect(() => {
    if (!profileKey || chosenHere.current) return;
    setPlace({ key: profileKey, source: 'profile' });
  }, [profileKey]);

  const pick = useCallback((key) => {
    const next = { key, source: 'pick' };
    chosenHere.current = true;
    setPlace(next);
    setLocateError('');
    save(next);
  }, []);

  const locate = useCallback(async () => {
    setLocating(true);
    setLocateError('');
    const { key, error } = await locateCity();
    setLocating(false);
    if (error) {
      setLocateError(error);
      return;
    }
    const next = { key, source: 'exact' };
    chosenHere.current = true;
    setPlace(next);
    save(next);
  }, []);

  return { cityKey: place.key, source: place.source, pick, locate, locating, locateError };
}
